// @tennis-matches/services/trainerstunden/trainer-payment-due-days
// Zahlungsziel (Frist in Tagen) je Trainer — unabhängig von Zahlungsmethode

import { addDaysToIsoDate } from "./billing-recipient-semantics.js";
import { isBillingStatementLiveDraft } from "./billing-statement-invoice-freeze.js";

function nz(v) {
  return String(v ?? "").trim();
}

export const TRAINER_DEFAULT_PAYMENT_DUE_DAYS = 7;

export const TRAINER_PAYMENT_DUE_DAYS_MIN = 1;
export const TRAINER_PAYMENT_DUE_DAYS_MAX = 366;

/** @deprecated Verwende {@link TRAINER_DEFAULT_PAYMENT_DUE_DAYS} */
export const PAYMENT_METHOD_DEFAULT_DUE_DAYS = TRAINER_DEFAULT_PAYMENT_DUE_DAYS;

/** @deprecated Verwende {@link validateTrainerPaymentDueDays} */
export function validatePaymentMethodDueDays(value) {
  return validateTrainerPaymentDueDays(value);
}

/**
 * @param {unknown} value
 * @returns {{ ok: true, dueDays: number } | { ok: false, reason: string }}
 */
export function validateTrainerPaymentDueDays(value) {
  const raw = String(value ?? "").trim();
  const n = raw === "" ? TRAINER_DEFAULT_PAYMENT_DUE_DAYS : Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n)) {
    return { ok: false, reason: "due-days-invalid" };
  }
  if (n < TRAINER_PAYMENT_DUE_DAYS_MIN || n > TRAINER_PAYMENT_DUE_DAYS_MAX) {
    return { ok: false, reason: "due-days-out-of-range" };
  }
  return { ok: true, dueDays: n };
}

/**
 * @param {object | null | undefined} trainer
 * @returns {number}
 */
export function resolveTrainerPaymentDueDays(trainer) {
  const tr = trainer && typeof trainer === "object" ? trainer : null;
  const n = Number(tr?.payment_due_days);
  if (Number.isFinite(n) && n >= TRAINER_PAYMENT_DUE_DAYS_MIN)
    return Math.floor(n);
  return TRAINER_DEFAULT_PAYMENT_DUE_DAYS;
}

/** @param {string} isoTimestamp */
export function isoDateFromLocalTimestamp(isoTimestamp) {
  const raw = nz(isoTimestamp);
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

export function localTodayIsoDate() {
  return isoDateFromLocalTimestamp(new Date().toISOString());
}

/**
 * Rechnungsdatum für Anzeige/PDF: Versand > Entwurf (heute) > Freeze > created_at > heute.
 * @param {object | null | undefined} statement
 */
export function resolveBillingInvoiceDateIso(statement) {
  const st = statement && typeof statement === "object" ? statement : {};
  if (isBillingStatementLiveDraft(st)) return localTodayIsoDate();
  const sent = nz(st.sent_at);
  if (sent) return isoDateFromLocalTimestamp(sent);
  const frozen = nz(st.invoice_frozen_at);
  if (frozen) return isoDateFromLocalTimestamp(frozen);
  const created = nz(st.created_at);
  if (created) return isoDateFromLocalTimestamp(created);
  return localTodayIsoDate();
}

/**
 * Zahlungsziel = Rechnungsdatum + payment_due_days des Rechnungsstellers.
 * @param {{ invoiceDateIso?: string, issuerTrainer?: object | null }} [opts]
 */
export function computeBillingStatementPaymentDueDate(opts = {}) {
  const statement =
    opts.statement && typeof opts.statement === "object"
      ? opts.statement
      : null;
  if (statement && isBillingStatementLiveDraft(statement)) {
    const stored = nz(statement.due_date).slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(stored)) return stored;
  }
  const invoiceDate = nz(opts.invoiceDateIso).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(invoiceDate)) return "";
  const dueDays = resolveTrainerPaymentDueDays(opts.issuerTrainer);
  return addDaysToIsoDate(invoiceDate, dueDays);
}

/**
 * Gespeichertes Fälligkeitsdatum oder berechnetes Zahlungsziel (Rechnungsdatum + Frist).
 * @param {object | null | undefined} statement
 * @param {{ issuerTrainer?: object | null }} [opts]
 */
export function resolveBillingStatementDueDateIso(statement, opts = {}) {
  const st = statement && typeof statement === "object" ? statement : {};
  const stored = nz(st.due_date).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(stored)) return stored;
  return computeBillingStatementPaymentDueDate({
    statement: st,
    invoiceDateIso: resolveBillingInvoiceDateIso(st),
    issuerTrainer: opts.issuerTrainer ?? null,
  });
}
